import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Switch, useHistory, useParams } from 'react-router-dom'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { Grid, Wrapper, Button, P } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'

import Header from 'components/product-header'
import NavigationTabs from 'components/navigation-tabs'
import Activity from 'components/activity'
import NotFoundMessage from 'components/not-found-message'
import { SentryRoute } from 'instrument'
import { FuneralPlan, FuneralPlanPaymentDetails } from 'lib/models/funeral-plan'
import {
  fetchFuneralPlan,
  fetchFuneralPlanPaymentDetails,
  fetchRedeemFuneralPlan,
  showModal,
  unsetProduct,
} from 'state/actions'
import { PRODUCTS } from 'utils/enums'

import CaseDetails from './case-details'
import ConvertedMessage from './converted-message'
import Documents from './documents'
import Payment from './payment'
import RedeemPlanModal from './redeem-plan-modal'

const getNavLinks = (funeralPlan: FuneralPlan) => {
  const navLinks = [
    { label: 'Plan details', url: `/funeral-plans/${funeralPlan.id}` },
    {
      label: 'Documents',
      url: `/funeral-plans/${funeralPlan.id}/documents`,
    },
    {
      label: 'Payment',
      url: `/funeral-plans/${funeralPlan.id}/payment`,
    },
    { label: 'Activity', url: `/funeral-plans/${funeralPlan.id}/activity` },
  ]

  return navLinks
}

const StyledWrapper = styled(Wrapper)`
  position: relative;
`

const RowWrapper = styled(Wrapper)`
  align-items: center;
  display: flex;
  margin: ${GTR.M} 0;
  gap: ${GTR.M};
  position: relative;
  justify-content: flex-start;
`

const FuneralPlanCase = (): React.ReactElement => {
  const { id: funeralPlanId } = useParams<{ id: string }>()
  const history = useHistory()
  const { funeralPlan, funeralPlanPaymentDetails } = useSelector(
    (state: {
      funeralPlanCase: FuneralPlan
      funeralPlanPaymentDetails: FuneralPlanPaymentDetails
    }) => ({
      funeralPlan: state.funeralPlanCase,
      funeralPlanPaymentDetails: state.funeralPlanPaymentDetails,
    }),
    shallowEqual
  )
  const [isRedeemed, setIsRedeemed] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(fetchFuneralPlan(funeralPlanId))
    dispatch(fetchFuneralPlanPaymentDetails(funeralPlanId))

    return () => {
      dispatch(unsetProduct('funeral-plan'))
    }
  }, [dispatch, funeralPlanId])

  useEffect(() => {
    dispatch(fetchFuneralPlan(funeralPlanId))
  }, [dispatch, funeralPlanId])

  const handlePlanRedeem = () => {
    dispatch(
      showModal({
        component: RedeemPlanModal,
        headingText: 'Redeem this funeral plan?',
        config: {
          /** For instalments, we will pass in amountToClearBalanceInPence instead, to take into account any removal of instalments fee */
          leftToPayInPence:
            funeralPlanPaymentDetails.attributes.type === 'Instalments'
              ? funeralPlanPaymentDetails.attributes.amountToClearBalanceInPence
              : funeralPlanPaymentDetails.attributes.leftToPayInPence,
          redeemPlan: async () => {
            await dispatch(fetchRedeemFuneralPlan(funeralPlanId))
            await dispatch(fetchFuneralPlan(funeralPlanId))
            setIsRedeemed(true)
          },
        },
      })
    )
  }

  const handleOpenCancelPlan = (): void => {
    history.push(`/funeral-plans/${funeralPlanId}/payment/cancel-plan`)
  }

  if (!funeralPlan.id) return <P size="L">Loading...</P>

  return (
    <StyledWrapper
      data-sticky-content="funeral-plan-case"
      padding={[GTR.XL, 0, 0]}
    >
      <Header
        attributes={funeralPlan.attributes}
        productType={PRODUCTS.FUNERAL_PLAN}
      />
      {funeralPlan.attributes.status === 'closed' &&
        funeralPlan.attributes?.funeralLead?.id && (
          <Wrapper margin={['M', 0, 0]}>
            <ConvertedMessage funeralPlan={funeralPlan} />
          </Wrapper>
        )}
      {funeralPlan?.attributes.status === 'active' && (
        <RowWrapper>
          <Button.Primary
            disabled={isRedeemed}
            onClick={() => handlePlanRedeem()}
          >
            Redeem Funeral Plan
          </Button.Primary>

          <Button.Danger onClick={handleOpenCancelPlan}>
            Cancel Funeral Plan
          </Button.Danger>
        </RowWrapper>
      )}
      <Wrapper data-cy="navigation-bar" separator>
        <NavigationTabs links={getNavLinks(funeralPlan)} />
      </Wrapper>
      <Grid separator>
        <Grid.Item span={8}>
          <Switch>
            <SentryRoute
              path="/funeral-plans/:id"
              exact
              component={CaseDetails}
            />
            <SentryRoute
              path="/funeral-plans/:id/documents"
              component={Documents}
            />
            <SentryRoute
              path="/funeral-plans/:id/payment"
              render={() => <Payment />}
            />
            <SentryRoute
              path="/funeral-plans/:id/activity"
              render={({ history, location, match }) => (
                <Activity
                  history={history}
                  location={location}
                  match={match}
                  leadId={funeralPlan?.attributes.leadId}
                  product={PRODUCTS.FUNERAL_PLAN}
                />
              )}
            />
            <SentryRoute render={() => <NotFoundMessage />} />
          </Switch>
        </Grid.Item>
      </Grid>
    </StyledWrapper>
  )
}

export default FuneralPlanCase
